import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../../utils/apiConfig';

// requiresHF: true → moteur cloud HuggingFace, désactivé si HF_TOKEN absent.
// Les moteurs locaux (PaddleOCR, Qwen Mac) ne dépendent pas de HF_TOKEN.
const ENGINES = [
  {
    key: 'paddleocr',
    label: 'PaddleOCR',
    description: 'Moteur local (Docker). Gratuit, pas de dependance externe.',
    cost: 'Gratuit',
    badge: 'Local',
    requiresHF: false
  },
  {
    key: 'qwen3-vl-local',
    label: 'Qwen2.5-VL (Mac Mini local)',
    description: 'VLM auto-heberge sur le Mac Mini du bureau (Ollama via tunnel SSH). Gratuit, prive (RGPD), insensible aux depreciations cloud.',
    cost: 'Gratuit',
    badge: 'Local IA',
    requiresHF: false
  },
  {
    key: 'gemma-3n',
    label: 'Gemma 3n (HuggingFace)',
    description: 'Google Gemma 3n via Together AI. Tres performant, ultra economique.',
    cost: '~0.000012 $/scan',
    badge: 'Recommande',
    requiresHF: true
  },
  {
    key: 'qwen3-vl',
    label: 'Qwen3-VL (HuggingFace)',
    description: 'Qwen3-VL 8B via Together AI. Bonne precision, economique.',
    cost: '~0.000015 $/scan',
    badge: 'Cloud',
    requiresHF: true
  }
];

const OCRSettingsPanel = ({ token }) => {
  const [currentEngine, setCurrentEngine] = useState('paddleocr');
  const [selectedEngine, setSelectedEngine] = useState('paddleocr');
  const [hfHealth, setHfHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Charger la config actuelle et le health HF en parallele
        const [configRes, hfRes] = await Promise.all([
          fetch(API_ENDPOINTS.ADMIN_CONFIG, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${API_ENDPOINTS.OCR_HF_HEALTH}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);

        if (configRes.ok) {
          const configData = await configRes.json();
          if (configData.success) {
            const ocrConfig = configData.data.find(c => c.key === 'OCR_PRIMARY_ENGINE');
            if (ocrConfig) {
              setCurrentEngine(ocrConfig.value);
              setSelectedEngine(ocrConfig.value);
            }
          }
        }

        if (hfRes.ok) {
          const hfData = await hfRes.json();
          setHfHealth(hfData);
        }
      } catch (err) {
        console.error('Erreur chargement config OCR:', err);
        setMessage({ type: 'error', text: 'Erreur lors du chargement' });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);

    // Verifier que HF_TOKEN est configure si moteur cloud HuggingFace
    const selected = ENGINES.find(e => e.key === selectedEngine);
    if (selected?.requiresHF && hfHealth && !hfHealth.configured) {
      setMessage({ type: 'error', text: 'HF_TOKEN non configure sur le serveur. Ajoutez-le dans le .env du backend.' });
      setSaving(false);
      return;
    }

    try {
      const response = await fetch(API_ENDPOINTS.ADMIN_CONFIG_BY_KEY('OCR_PRIMARY_ENGINE'), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ value: selectedEngine })
      });

      const result = await response.json();
      if (result.success) {
        setCurrentEngine(selectedEngine);
        setMessage({ type: 'success', text: `Moteur OCR mis a jour : ${selectedEngine}` });
      } else {
        setMessage({ type: 'error', text: result.error || 'Erreur' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur de sauvegarde' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
        Chargement de la configuration OCR...
      </div>
    );
  }

  const hasChanges = selectedEngine !== currentEngine;

  return (
    <div style={{ maxWidth: '700px' }}>
      <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#333' }}>
        Configuration du moteur OCR
      </h3>
      <p style={{ margin: '0 0 24px 0', fontSize: '14px', color: '#666' }}>
        Selectionnez le moteur principal pour l'extraction des tickets de frais.
        En cas d'echec du moteur cloud, le systeme bascule automatiquement sur PaddleOCR puis Tesseract.
      </p>

      {/* Statut HF_TOKEN */}
      <div style={{
        padding: '12px 16px',
        marginBottom: '20px',
        borderRadius: '8px',
        background: hfHealth?.configured ? '#f0fdf4' : '#fef3c7',
        border: `1px solid ${hfHealth?.configured ? '#bbf7d0' : '#fde68a'}`,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        fontSize: '13px'
      }}>
        <span style={{
          width: '8px', height: '8px', borderRadius: '50%',
          background: hfHealth?.configured && hfHealth?.authenticated ? '#22c55e' : hfHealth?.configured ? '#f59e0b' : '#ef4444',
          flexShrink: 0
        }} />
        <span style={{ color: '#374151' }}>
          {hfHealth?.configured && hfHealth?.authenticated
            ? 'HF_TOKEN configure et authentifie'
            : hfHealth?.configured
              ? 'HF_TOKEN configure mais non authentifie'
              : 'HF_TOKEN non configure — les moteurs cloud ne sont pas disponibles'}
        </span>
      </div>

      {/* Liste des moteurs */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
        {ENGINES.map(engine => {
          const isSelected = selectedEngine === engine.key;
          const isCurrent = currentEngine === engine.key;
          const isDisabled = engine.requiresHF && hfHealth && !hfHealth.configured;

          return (
            <label
              key={engine.key}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                padding: '16px',
                borderRadius: '10px',
                border: `2px solid ${isSelected ? '#9c27b0' : '#e5e7eb'}`,
                background: isSelected ? '#faf5ff' : isDisabled ? '#f9fafb' : 'white',
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                opacity: isDisabled ? 0.5 : 1,
                transition: 'all 0.2s ease'
              }}
            >
              <input
                type="radio"
                name="ocr-engine"
                value={engine.key}
                checked={isSelected}
                disabled={isDisabled}
                onChange={() => setSelectedEngine(engine.key)}
                style={{ marginTop: '3px' }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <span style={{ fontWeight: 600, fontSize: '14px', color: '#333' }}>
                    {engine.label}
                  </span>
                  {isCurrent && (
                    <span style={{
                      padding: '2px 8px', borderRadius: '12px', fontSize: '11px',
                      background: '#9c27b0', color: 'white', fontWeight: 500
                    }}>
                      Actif
                    </span>
                  )}
                  <span style={{
                    padding: '2px 8px', borderRadius: '12px', fontSize: '11px',
                    background: engine.badge === 'Recommande' ? '#dcfce7' : engine.badge === 'Local' ? '#e0f2fe' : engine.badge === 'Local IA' ? '#d1fae5' : '#f3e8ff',
                    color: engine.badge === 'Recommande' ? '#166534' : engine.badge === 'Local' ? '#0c4a6e' : engine.badge === 'Local IA' ? '#065f46' : '#6b21a8',
                    fontWeight: 500
                  }}>
                    {engine.badge}
                  </span>
                </div>
                <p style={{ margin: '0 0 4px 0', fontSize: '13px', color: '#666' }}>
                  {engine.description}
                </p>
                <span style={{ fontSize: '12px', color: '#9c27b0', fontWeight: 500 }}>
                  {engine.cost}
                </span>
              </div>
            </label>
          );
        })}
      </div>

      {/* Message */}
      {message && (
        <div style={{
          padding: '10px 14px',
          marginBottom: '16px',
          borderRadius: '8px',
          fontSize: '13px',
          background: message.type === 'success' ? '#f0fdf4' : '#fef2f2',
          color: message.type === 'success' ? '#166534' : '#991b1b',
          border: `1px solid ${message.type === 'success' ? '#bbf7d0' : '#fecaca'}`
        }}>
          {message.text}
        </div>
      )}

      {/* Bouton sauvegarder */}
      <button
        onClick={handleSave}
        disabled={!hasChanges || saving}
        style={{
          padding: '10px 24px',
          borderRadius: '8px',
          border: 'none',
          background: hasChanges ? 'linear-gradient(135deg, #9c27b0, #7b1fa2)' : '#e5e7eb',
          color: hasChanges ? 'white' : '#9ca3af',
          fontSize: '14px',
          fontWeight: 600,
          cursor: hasChanges ? 'pointer' : 'not-allowed',
          transition: 'all 0.2s ease'
        }}
      >
        {saving ? 'Enregistrement...' : 'Enregistrer'}
      </button>
    </div>
  );
};

export default OCRSettingsPanel;
